import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, type SupportedLanguage } from '@/i18n';

interface LanguageSwitcherProps {
  /** Custom class name */
  className?: string;
  /** Dark variant for dark backgrounds */
  variant?: 'light' | 'dark';
}

/**
 * Language switcher component for Operator PWA.
 * Shows all available languages as a selectable list.
 */
export function LanguageSwitcher({ className = '', variant = 'light' }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation('common');
  const currentLanguage = (i18n.language?.split('-')[0] || 'en') as SupportedLanguage;

  const handleLanguageChange = (lang: SupportedLanguage) => {
    i18n.changeLanguage(lang);
  };

  const isDark = variant === 'dark';

  return (
    <div className={`${isDark ? 'bg-white/10 border-white/20' : 'bg-white border-neutral-light'} border rounded-xl overflow-hidden ${className}`}>
      <div className={`px-4 py-3 border-b ${isDark ? 'border-white/20' : 'border-neutral-light'}`}>
        <div className={`flex items-center gap-2 ${isDark ? 'text-white/70' : 'text-neutral-medium'}`}>
          <Globe className="h-5 w-5" />
          <span className="text-sm font-medium">{t('settings.language', 'Language')}</span>
        </div>
      </div>
      {SUPPORTED_LANGUAGES.map((lang) => (
        <button
          key={lang}
          onClick={() => handleLanguageChange(lang)}
          className={`w-full flex items-center justify-between px-4 py-3 border-b last:border-0 transition-colors
            ${isDark ? 'border-white/20 hover:bg-white/10' : 'border-neutral-light hover:bg-neutral-light/30'}`}
          aria-label={t('accessibility.switchLanguage', { language: LANGUAGE_NAMES[lang] })}
          aria-current={currentLanguage === lang ? 'true' : undefined}
        >
          <span className={currentLanguage === lang
            ? (isDark ? 'text-secondary font-medium' : 'text-primary font-medium')
            : (isDark ? 'text-white' : 'text-neutral-dark')
          }>
            {LANGUAGE_NAMES[lang]}
          </span>
          {currentLanguage === lang && (
            <Check className={`h-5 w-5 ${isDark ? 'text-secondary' : 'text-primary'}`} />
          )}
        </button>
      ))}
    </div>
  );
}
